import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import moment from 'moment';
import { useLoadingBar } from 'react-top-loading-bar';
import ContentLoader from '../loader/ContentLoader';
import LogsTabs from './LogsTabs';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { logsService } from '../../services/logs.service';

const MySwal = withReactContent(Swal);

/* ---------- styles ---------- */
const pageBtn = (active, disabled) => ({
  minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
  border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
  background: active ? "var(--page-active-bg)" : "#fff",
  color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
  fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
});
const chip = (color, bg, border) => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
  borderRadius: 10, background: bg, border: "1px solid " + border, color, fontSize: 13
});
const iconBtn = (bg, color) => ({
  width: 32, height: 32, borderRadius: 8, background: bg, color, border: 'none', padding: 0
});
const pill = (bg, color) => ({
  display: "inline-block", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12,
  background: bg, color, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap"
});
const thStyle = {
  fontSize: "11px", fontWeight: "700", color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.6px",
  borderBottom: "2px solid #e8edf5", padding: "10px 12px",
  whiteSpace: "nowrap", background: "#f8f9fe"
};
const monoStyle = { fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 12 };

const METHOD_COLORS = {
  GET: ["#e6f7f1", "#0f8a5f"],
  POST: ["#e8f0fe", "var(--btn-bg)"],
  PUT: ["#fff4e6", "#e8590c"],
  PATCH: ["#f3ebfa", "var(--btn-hover-bg)"],
  DELETE: ["#fdecec", "#d64545"],
};

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm:ss A") : "-");
const formatDuration = (ms) => (ms == null ? "-" : ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`);
const isSuccess = (code) => code >= 200 && code < 300;

const ServiceApiLogList = () => {
  const { start, complete } = useLoadingBar();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const totalRecords = pagination?.total || 0;
  const totalPage = pagination?.totalPages || 1;
  const successCount = items.filter(i => isSuccess(i?.response_status)).length;
  const avgDuration = items.length ? Math.round(items.reduce((sum, i) => sum + (Number(i?.duration_ms) || 0), 0) / items.length) : 0;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  useEffect(() => {
    loadItems();
  }, [pagingLimit, pageValue]);

  /* ---------- API: list ---------- */
  const loadItems = async () => {
    try {
      setLoading(true);
      start();
      const res = await logsService.serviceApi({ page: Number(pageValue), limit: Number(pagingLimit) });
      if (res?.success) {
        setItems(res.resData?.data || []);
        setPagination(res.resData?.pagination || { total: 0, totalPages: 1 });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      complete();
    }
  };

  /* ---------- pagination ---------- */
  const handlePagingLimit = (e) => { setPageValue(1); setPagingLimit(e.target.value); };
  const handlePagination = (n) => setPageValue(n);
  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) setPageValue(n);
    setGoPage("");
  };
  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const methodPill = (m) => {
    const [bg, color] = METHOD_COLORS[m] || ["#f1f3f9", "#6b7a90"];
    return <span style={pill(bg, color)}>{m || "-"}</span>;
  };
  const statusPill = (code) => isSuccess(code)
    ? <span style={pill("#e6f7f1", "#0f8a5f")}>{code}</span>
    : <span style={pill(code >= 500 ? "#fdecec" : "#fff4e6", code >= 500 ? "#d64545" : "#e8590c")}>{code ?? "-"}</span>;
  const durationText = (ms) => (
    <span style={{ ...monoStyle, fontWeight: 600, color: ms >= 3000 ? "#d64545" : ms >= 1000 ? "#e8590c" : "#0f8a5f" }}>{formatDuration(ms)}</span>
  );

  /* ---------- export ---------- */
  const csvHeaders = [
    { label: "ID", key: "id" },
    { label: "Date", key: "created_at" },
    { label: "Agent Code", key: "agent_code" },
    { label: "User ID", key: "user_id" },
    { label: "HTTP Method", key: "http_method" },
    { label: "Endpoint", key: "endpoint" },
    { label: "Response Status", key: "response_status" },
    { label: "Duration (ms)", key: "duration_ms" },
    { label: "Aborted", key: "aborted" },
    { label: "IP Address", key: "ip_address" },
    { label: "Request ID", key: "request_id" },
  ];

  return (
    <section className="section dashboard">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Service API Logs</h4>
          <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
            <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/logs" style={{ color: "#475569" }}>Logs</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <span className="fw-semibold" style={{ color: "#4a5568" }}>Service API Logs</span>
          </nav>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" onClick={loadItems} className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          <CSVLink data={items} headers={csvHeaders} filename="service-api-logs.csv"
            className="btn btn-sm d-flex align-items-center gap-1 px-3"
            style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
            <i className="bi bi-download"></i> Export
          </CSVLink>
        </div>
      </div>

      <LogsTabs />

      {/* Stat chips */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total Calls</span>
        <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{successCount}</b> Successful on this page</span>
        <span style={chip("#d64545", "#fdecec", "#f5c2c2")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{items.length - successCount}</b> Failed on this page</span>
        <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}><i className="bi bi-stopwatch" style={{ fontSize: 12 }}></i><b>{formatDuration(avgDuration)}</b> Avg duration on this page</span>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
        <div style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.6)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10
            }}>
              <ContentLoader loaderType="circleSpin" />
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  {["S.No.", "Date", "Agent", "Request", "Status", "Duration", "IP Address", "Actions"].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row, sno_i) => (
                  <tr key={row?.id}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                    <td style={{ fontSize: 12, color: "#4a5568", whiteSpace: "nowrap" }}>{formatDate(row?.created_at)}</td>
                    <td>
                      <div className="fw-semibold" style={{ fontSize: 13, color: "#1a202c" }}>{row?.agent_code || "-"}</div>
                      <div style={{ fontSize: 11, color: "#475569" }}>User #{row?.user_id ?? "-"}</div>
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <div className="d-flex align-items-center gap-2">
                        {methodPill(row?.http_method)}
                        <span style={{ ...monoStyle, color: "#1a202c" }}>{row?.endpoint || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        {statusPill(row?.response_status)}
                        {row?.aborted && <span style={pill("#fdecec", "#d64545")}>Aborted</span>}
                      </div>
                    </td>
                    <td>{durationText(row?.duration_ms)}</td>
                    <td style={{ ...monoStyle, whiteSpace: "nowrap" }}>{row?.ip_address || "-"}</td>
                    <td className="text-center">
                      <Link to={`/logs/service-api/${row?.id}`} className="btn btn-sm d-inline-flex align-items-center justify-content-center" title="View details"
                        style={iconBtn('#e6f7f1', '#0f8a5f')}>
                        <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                      </Link>
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">No records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
          <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> logs</div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span>Rows</span>
            <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
              {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
            </select>
            <span>Go</span>
            <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
              value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
            <span>/ {totalPage}</span>
            <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
            <div className="d-flex align-items-center gap-1">
              <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
              <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
              {getPageNumbers().map((n, i) => n === "..."
                ? <span key={"e" + i} className="px-1">...</span>
                : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
              <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
              <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
            </div>
          </div>
        </div>
      </div>

    </section>
  )
}

export default ServiceApiLogList;
